import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Avatar,
  HandRightIcon,
  Icon,
  IconButton,
  InlineAlert,
  Pane,
  Strong,
  Text,
  ThumbsDownIcon,
  ThumbsUpIcon,
  TimeIcon,
  toaster,
  TrashIcon,
} from "evergreen-ui";
import AutocompleteFormField from "../AutocompleteFormField";
import PromiseButton from "../PromiseButton";
import useControlHub from "../../hooks/useControlHub";
import { BILL_APPROVE_STATUS } from "../../utils/constants";

const VEREDICTS = {
  approved: {
    icon: ThumbsUpIcon,
    value: "approved",
  },
  rejected: {
    icon: ThumbsDownIcon,
    value: "rejected",
  },
  pending: {
    value: "pending",
  },
  on_hold: {
    icon: TimeIcon,
    value: "on_hold",
  },
  halted: {
    value: "halted",
  },
  delegated: {
    value: "delegated",
  },
  master_approved: {
    value: "master_approved",
  },
};

const Approver = ({
  data,
  hasDelete,
  isEditable,
  onDelete,
  hasSortDown,
  onSortDown,
  hasSortUp,
  onSortUp,
  hasVeredict,
  onApprove,
}) => {
  const { api, user } = useControlHub();
  const { invoice_id } = useParams();

  const approverPingPromise = useCallback(() => {
    return new Promise((resolve, reject) => {
      api
        .pingBillApprover({ billID: invoice_id })
        .then((data) => {
          if (!data || !data.success) {
            throw new Error("Unable to ping next approver");
          }
          toaster.success("Approver will be notified to approve");
          resolve();
        })
        .catch((error) => {
          toaster.warning(error.message);
          reject();
        });
    });
  }, [api, invoice_id]);

  const approverApprovePromise = useCallback(() => {
    return new Promise((resolve, reject) => {
      api
        .approveBill({ billID: invoice_id })
        .then((data) => {
          if (!data.success) {
            throw new Error("Unable to approve bill:" + (data.message || "-"));
          }
          toaster.success("Your approval has been recorded");
          onApprove();
          resolve();
        })
        .catch((error) => {
          toaster.danger(error.message);
          reject();
        });
    });
  }, [api, invoice_id, onApprove]);

  const actionsPanel = useMemo(
    () => (
      <>
        <IconButton
          appearance="minimal"
          aria-label="Delete Approver"
          disabled={!hasDelete}
          height={24}
          icon={TrashIcon}
          marginRight={8}
          onClick={onDelete}
          title="Delete Approver"
        />
        <IconButton
          appearance="minimal"
          aria-label="Move Approver up"
          disabled={!hasSortUp}
          height={24}
          icon={ArrowUpIcon}
          marginRight={8}
          onClick={onSortUp}
        />
        <IconButton
          appearance="minimal"
          aria-label="Move Approver down"
          disabled={!hasSortDown}
          height={24}
          icon={ArrowDownIcon}
          marginRight={8}
          onClick={onSortDown}
        />
      </>
    ),
    [hasDelete, hasSortDown, hasSortUp, onDelete, onSortDown, onSortUp]
  );

  return (
    <Pane alignItems="center" display="flex" marginY={16}>
      {isEditable ? actionsPanel : null}
      <Avatar src={data["image_48"]} />
      <Strong marginX={8}>
        {data["display_name"] || data["full_name"] || data["slack_full_name"]}
      </Strong>
      {hasVeredict && data.veredict === VEREDICTS.delegated.value ? (
        <Text size={300} color="muted">
          delegated
        </Text>
      ) : null}
      {hasVeredict &&
      data.veredict in VEREDICTS &&
      VEREDICTS[data.veredict].icon ? (
        <Icon
          color="muted"
          icon={VEREDICTS[data.veredict].icon}
          marginX={4}
          size={16}
          title={data.veredict}
        />
      ) : null}
      {data.veredict === VEREDICTS.pending.value ? (
        user.id === data["id"] ? (
          <PromiseButton
            height={24}
            iconBefore={ThumbsUpIcon}
            promiser={approverApprovePromise}
          >
            Approve
          </PromiseButton>
        ) : (
          <PromiseButton
            height={24}
            iconBefore={HandRightIcon}
            promiser={approverPingPromise}
          >
            Ping
          </PromiseButton>
        )
      ) : null}
    </Pane>
  );
};

const ApproversInput = ({ approvers, isEditable, onDataChange, status }) => {
  const { api } = useControlHub();
  const [users, setUsers] = useState();

  useEffect(() => {
    api
      .getUsers()
      .then((data) => {
        if (!data) {
          throw new Error("Couldn't fetch users");
        }
        setUsers(
          data.reduce((prev, curr) => ({ ...prev, [curr.id]: curr }), {})
        );
      })
      .catch(() => {
        setUsers();
      });
  }, [api]);

  const handleApproverAdd = useCallback(
    (approver) => {
      if (!approver || !approvers) {
        return;
      }
      if (!approvers.some(({ id }) => approver.id === id)) {
        onDataChange({ approvers: [...approvers, approver] });
      } else {
        toaster.notify("That approver is already on the list");
      }
    },
    [approvers, onDataChange]
  );

  const handleApproverApprove = useCallback(
    (approver) => () => {
      onDataChange({ fetch: true });
    },
    [onDataChange]
  );

  const handleApproverDelete = useCallback(
    ({ id }) =>
      () => {
        onDataChange({
          approvers: approvers.filter((approver) => approver.id !== id),
        });
      },
    [approvers, onDataChange]
  );

  const handleApproverSortDown = useCallback(
    ({ approver, approvers }) =>
      () => {
        const approverIndex = approvers.findIndex(
          ({ id }) => approver.id === id
        );
        const newApproversList = [];
        for (let index = 0; index < approvers.length; index++) {
          if (approverIndex === index) {
            newApproversList.push(approvers[index + 1]);
          } else if (approverIndex + 1 === index) {
            newApproversList.push(approver);
          } else {
            newApproversList.push(approvers[index]);
          }
        }
        onDataChange({ approvers: newApproversList });
      },
    [onDataChange]
  );

  const handleApproverSortUp = useCallback(
    ({ approver, approvers }) =>
      () => {
        const approverIndex = approvers.findIndex(
          (_approver) => approver.id === _approver.id
        );
        const newApproversList = [];
        for (let index = 0; index < approvers.length; index++) {
          if (approverIndex - 1 === index) {
            newApproversList.push(approver);
          } else if (approverIndex === index) {
            newApproversList.push(approvers[index - 1]);
          } else {
            newApproversList.push(approvers[index]);
          }
        }
        onDataChange({ approvers: newApproversList });
      },
    [onDataChange]
  );

  const userToString = useCallback(
    (user) => (user ? user["full_name"] || user["slack_full_name"] : ""),
    []
  );

  const approverIsSortable = useCallback(
    ({ approver, status }) =>
      approver.veredict === VEREDICTS.on_hold.value &&
      (status === BILL_APPROVE_STATUS.opened ||
        status === BILL_APPROVE_STATUS.processingAutomatic ||
        status === BILL_APPROVE_STATUS.processingManual),
    []
  );

  const approverCanMoveDown = useCallback(
    ({ approver, approvers, status }) => {
      const approverIndex = approvers.findIndex(
        (_approver) => approver.id === _approver.id
      );
      return (
        approverIsSortable({ approver, status }) &&
        approverIndex < approvers.length - 1
      );
    },
    [approverIsSortable]
  );

  const approverCanMoveUp = useCallback(
    ({ approver, approvers, status }) => {
      const approverIndex = approvers.findIndex(
        (_approver) => approver.id === _approver.id
      );
      return (
        approverIsSortable({ approver, status }) &&
        approverIndex > 0 &&
        approverIsSortable({ approver: approvers[approverIndex - 1], status })
      );
    },
    [approverIsSortable]
  );

  const addApprover = useMemo(() => {
    if (!users) {
      return null;
    }
    return (
      <Pane marginTop={32}>
        <AutocompleteFormField
          items={Object.values(users)}
          itemToString={userToString}
          label="Add approver"
          onChange={handleApproverAdd}
        />
      </Pane>
    );
  }, [handleApproverAdd, userToString, users]);

  if (!approvers || !users) {
    return null;
  }

  return (
    <Pane aria-label="approvers list">
      {approvers && approvers.length ? (
        <>
          {approvers.map((approver) => (
            <Approver
              data={{ ...users[approver.id], ...approver }}
              hasDelete={
                approvers.length > 1 &&
                approver.veredict === VEREDICTS.on_hold.value &&
                (status === BILL_APPROVE_STATUS.opened ||
                  status === BILL_APPROVE_STATUS.processingAutomatic ||
                  status === BILL_APPROVE_STATUS.processingManual)
              }
              isEditable={isEditable}
              key={approver.id}
              onApprove={handleApproverApprove(approver)}
              hasSortDown={approverCanMoveDown({ approver, approvers, status })}
              onSortDown={handleApproverSortDown({ approver, approvers })}
              hasSortUp={approverCanMoveUp({ approver, approvers, status })}
              onSortUp={handleApproverSortUp({ approver, approvers })}
              hasVeredict={
                status !== BILL_APPROVE_STATUS.opened &&
                status !== BILL_APPROVE_STATUS.processingAutomatic &&
                status !== BILL_APPROVE_STATUS.processingManual
              }
              onDelete={handleApproverDelete(approver)}
            />
          ))}
          {isEditable ? addApprover : null}
        </>
      ) : (
        <InlineAlert>There is no Approvers list available yet</InlineAlert>
      )}
    </Pane>
  );
};

export default ApproversInput;
